import { NewCard } from "@components/Cards/Templates/newCard";
import { TENANT } from "@types/tenant";
import { sendRegisterDeviceRequest } from "@services/API/Requests/DeviceResgistrationRequests";
import { toast } from "react-toastify";
import { useState } from "react";
import PasswordResetModal from "../../../../Modals/PasswordResetModal";
import WarningModal from "../../../../Modals/WarningModal";

interface SecuritySettingsCardProps {
  tenantData: TENANT;
  updateTenantData: (data: TENANT) => void;
  loading: boolean;
  setError: (error: any) => void;
  fromViewPage?: boolean;
}

export const SecuritySettingsCard: React.FC<SecuritySettingsCardProps> = ({
  tenantData,
  updateTenantData,
  loading,
  setError,
  fromViewPage = false,
}: SecuritySettingsCardProps) => {
  const errorToast = (msg: string) => toast.error(msg);

  const [password, setPassword] = useState("");
  const [passwordResetModalOpen, setPasswordResetModalOpen] = useState(false);
  const [certDownloading, setCertDownloading] = useState(false);

  const [warningModalOpen, setWarningModalOpen] = useState(false);

  const downloadClientCertificate = async (password: string) => {
    if (warningModalOpen) {
      setWarningModalOpen(false);
    }

    setCertDownloading(true);
    const result = await sendRegisterDeviceRequest(
      tenantData.id,
      tenantData.code,
      password || "",
      tenantData.active_certificates || false,
    );
    if (result?.error) {
      setError(result.error);
      errorToast(result.error);
    } else {
      updateTenantData("active_certificates", true);
    }
    setCertDownloading(false);
    setPassword("");
  };

  const handlePasswordResetRequest = (password: string) => {
    setPassword(password);
    if (!tenantData.active_certificates) {
      downloadClientCertificate(password);
    } else {
      setPasswordResetModalOpen(false);
      setWarningModalOpen(true);
    }
  };

  return (
    <>
      <NewCard
        header="セキュリティ設定"
        bodyLabel=""
        loading={loading}
        bodyContents={
          <div>
            <h2 className="mb-6 label-light-blue-xl">ログイン方法設定</h2>
            <label className="flex items-center text-xl ml-5 mb-2">
              <input
                type="checkbox"
                className="mr-5 w-5 h-5 accent-blue-600"
                checked={tenantData?.local_auth}
                onChange={() =>
                  updateTenantData("local_auth", !tenantData?.local_auth)
                }
              />
              ローカル認証
            </label>

            <div className="mt-5 divider" />

            <h2 className="mt-6 mb-6 label-light-blue-xl">電話帳参照制限</h2>
            <label className="flex items-center text-xl ml-5 mb-2">
              <input
                type="checkbox"
                className="mr-5 w-5 h-5 accent-blue-600"
                checked={tenantData?.require_passcode_for_phonebook}
                onChange={() =>
                  updateTenantData(
                    "require_passcode_for_phonebook",
                    !tenantData?.require_passcode_for_phonebook,
                  )
                }
              />
              電話帳参照時にパスコードの入力を求める
            </label>

            <label className="flex items-center text-xl ml-5 mb-2">
              <input
                type="checkbox"
                className="mr-5 w-5 h-5 accent-blue-600"
                checked={tenantData?.auth_by_phone_number}
                onChange={() =>
                  updateTenantData(
                    "auth_by_phone_number",
                    !tenantData?.auth_by_phone_number,
                  )
                }
              />
              電話番号認証
            </label>

            {fromViewPage && (
              <>
                <div className="mt-5 divider" />

                <h2 className="mt-6 mb-6 label-light-blue-xl">
                  クライアント証明設定
                </h2>
                <div>
                  <label className="flex items-center text-xl ml-5 mb-2">
                    <input
                      type="checkbox"
                      className="mr-5 w-5 h-5 accent-blue-600"
                      checked={tenantData?.certificate_auth_enabled}
                      onChange={() =>
                        updateTenantData(
                          "certificate_auth_enabled",
                          !tenantData?.certificate_auth_enabled,
                        )
                      }
                    />
                    クライアント証明
                  </label>
                  <div>
                    {!tenantData?.active_certificates &&
                      tenantData?.certificate_auth_enabled && (
                        <div
                          className={`flex justify-center ml-14 mt-2 mb-2 w-55 bg-blue-500 text-white px-4 py-4 rounded-lg items-center hover:bg-blue-400 hover:cursor-pointer disabled:bg-slate-400 disabled:cursor-not-allowed ${
                            certDownloading
                              ? "opacity-50 cursor-not-allowed"
                              : ""
                          }`}
                          onClick={() => setPasswordResetModalOpen(true)}
                        >
                          クライアント証明発行
                        </div>
                      )}
                    {tenantData?.active_certificates &&
                      tenantData?.certificate_auth_enabled && (
                        <div
                          className={`flex justify-center ml-14 mt-2 mb-2 w-60 bg-blue-500 text-white px-4 py-4 rounded-lg items-center hover:bg-blue-400 hover:cursor-pointer disabled:bg-slate-400 disabled:cursor-not-allowed ${
                            certDownloading
                              ? "opacity-50 cursor-not-allowed"
                              : ""
                          }`}
                          onClick={() => setPasswordResetModalOpen(true)}
                        >
                          証明書再発行
                        </div>
                      )}
                  </div>
                </div>
              </>
            )}
          </div>
        }
      />
      <PasswordResetModal
        isOpen={passwordResetModalOpen}
        onRequestClose={function (): void {
          setPasswordResetModalOpen(false);
        }}
        onPasswordResetSuccess={handlePasswordResetRequest}
        isReset={tenantData?.active_certificates}
        confirmButtonText={"発行する"}
      />
      <WarningModal
        title={"証明書パスワードリセット"}
        body={
          "再発行すると、以前の証明書は削除され、利用できなくなります。\nよろしいですか？"
        }
        confirmText={"はい"}
        isOpen={warningModalOpen}
        onRequestClose={() => setWarningModalOpen(false)}
        onConfirm={() => downloadClientCertificate(password)}
        confirmButtonColor="bg-blue-500"
      />
    </>
  );
};
